import React, { useEffect, useState, useMemo } from "react";
import { Barbell, Trash, Plus, X, PlayCircle, MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SPORTS = [
  { key: "", label: "ALL SPORTS" },
  { key: "football", label: "Football" },
  { key: "basketball", label: "Basketball" },
  { key: "cricket", label: "Cricket" },
  { key: "tennis", label: "Tennis" },
  { key: "swimming", label: "Swimming" },
  { key: "athletics", label: "Athletics" },
  { key: "hockey", label: "Hockey" },
  { key: "rugby", label: "Rugby" },
  { key: "badminton", label: "Badminton" },
  { key: "volleyball", label: "Volleyball" },
  { key: "boxing", label: "Boxing" },
  { key: "kabaddi", label: "Kabaddi" },
  { key: "general", label: "General" },
];
const DIFFICULTIES = ["", "beginner", "intermediate", "advanced"];

const emptyForm = {
  name: "", sport: "football", category: "technical", difficulty: "intermediate",
  duration_min: 15, players: "", equipment: "", instructions: "", coach_cues: "",
  video_url: "", tags: "",
};

export default function SportsDrills() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sport, setSport] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const [source, setSource] = useState("");
  const [q, setQ] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (sport) params.set("sport", sport);
    if (difficulty) params.set("difficulty", difficulty);
    if (source) params.set("source", source);
    if (q.trim()) params.set("q", q.trim());
    api.get(`/sports-coach/drills?${params.toString()}`)
      .then(({ data }) => setRows(data.items || []))
      .finally(() => setLoading(false));
  };
  useEffect(load, [sport, difficulty, source]);

  const grouped = useMemo(() => {
    const m = new Map();
    for (const r of rows) {
      const k = r.sport || "general";
      if (!m.has(k)) m.set(k, []);
      m.get(k).push(r);
    }
    return Array.from(m.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [rows]);

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    try {
      await api.post("/sports-coach/drills", {
        name: form.name.trim(),
        sport: form.sport,
        category: form.category,
        difficulty: form.difficulty,
        duration_min: Number(form.duration_min) || 15,
        players: form.players,
        equipment: form.equipment.split(",").map((x) => x.trim()).filter(Boolean),
        instructions: form.instructions,
        coach_cues: form.coach_cues.split("\n").map((x) => x.trim()).filter(Boolean),
        video_url: form.video_url.trim() || null,
        tags: form.tags.split(",").map((x) => x.trim()).filter(Boolean),
      });
      toast.success("Drill added");
      setShowForm(false);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setSaving(false);
    }
  };

  const removeDrill = async (id) => {
    if (!window.confirm("Delete this drill?")) return;
    await api.delete(`/sports-coach/drills/${id}`);
    toast.success("Deleted");
    load();
  };

  const uploadVideo = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/sports-coach/drills/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((prev) => ({ ...prev, video_url: `${BACKEND_URL}${data.url}` }));
      toast.success("Video uploaded — URL attached");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  return (
    <div data-testid="sc-drills">
      <div className="flex items-center justify-between mb-4 gap-2 flex-wrap">
        <div>
          <div className="label-tiny text-slate-500">// DRILLS LIBRARY</div>
          <div className="text-sm text-slate-600">{rows.length} drills · filter by sport, then attach to sessions or share with athletes.</div>
        </div>
        <button onClick={() => setShowForm((v) => !v)} className="flex items-center gap-2 bg-cyan-600 hover:bg-cyan-700 text-white text-xs label-tiny tracking-wider px-4 py-2" data-testid="sc-drills-new-btn">
          {showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "CANCEL" : "NEW DRILL"}
        </button>
      </div>

      {/* Filter row */}
      <div className="border border-slate-200 bg-white p-3 mb-4 flex flex-wrap gap-2 items-center">
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="text-slate-400" />
          <input
            className="flex-1 text-sm bg-transparent focus:outline-none"
            placeholder="Search name, tag, instructions…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && load()}
            data-testid="sc-drills-search"
          />
        </div>
        <select value={sport} onChange={(e) => setSport(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-xs bg-white" data-testid="sc-drills-sport">
          {SPORTS.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select value={difficulty} onChange={(e) => setDifficulty(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-xs bg-white" data-testid="sc-drills-difficulty">
          {DIFFICULTIES.map((d) => <option key={d} value={d}>{d ? d.toUpperCase() : "ALL LEVELS"}</option>)}
        </select>
        <select value={source} onChange={(e) => setSource(e.target.value)} className="border border-slate-300 px-2 py-1.5 text-xs bg-white" data-testid="sc-drills-source">
          <option value="">ALL SOURCES</option>
          <option value="seed">BUILT-IN</option>
          <option value="custom">MY DRILLS</option>
        </select>
      </div>

      {/* New-drill form */}
      {showForm && (
        <div className="border border-slate-200 bg-white p-4 mb-6 space-y-3" data-testid="sc-drills-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Drill name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sc-drill-name" />
            <select value={form.sport} onChange={(e) => setForm({ ...form, sport: e.target.value })} className="border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="sc-drill-sport">
              {SPORTS.filter((s) => s.key).map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <input className="border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Category (technical, tactical, ...)" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} />
            <select value={form.difficulty} onChange={(e) => setForm({ ...form, difficulty: e.target.value })} className="border border-slate-300 px-3 py-2 text-sm bg-white">
              {DIFFICULTIES.filter(Boolean).map((d) => <option key={d} value={d}>{d.toUpperCase()}</option>)}
            </select>
            <input type="number" className="border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Duration (min)" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} />
            <input className="border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Players (e.g. 4, pairs, squad)" value={form.players} onChange={(e) => setForm({ ...form, players: e.target.value })} />
          </div>
          <input className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Equipment (comma separated)" value={form.equipment} onChange={(e) => setForm({ ...form, equipment: e.target.value })} />
          <textarea className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" rows={3} placeholder="Instructions (setup + execution)" value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} />
          <textarea className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" rows={3} placeholder="Coach cues (one per line)" value={form.coach_cues} onChange={(e) => setForm({ ...form, coach_cues: e.target.value })} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input className="border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Video URL (YouTube / self-hosted mp4)" value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} data-testid="sc-drill-video-url" />
            <div className="flex items-center gap-2">
              <label className="text-xs text-slate-500">Or upload video:</label>
              <input type="file" accept="video/*,image/*" onChange={uploadVideo} disabled={uploading} className="text-xs" data-testid="sc-drill-video-upload" />
              {uploading && <span className="text-xs text-cyan-600">uploading…</span>}
            </div>
          </div>
          <input className="w-full border border-slate-300 px-3 py-2 text-sm bg-white" placeholder="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
          <div className="flex justify-end">
            <button onClick={submit} disabled={saving} className="bg-cyan-600 hover:bg-cyan-700 disabled:opacity-50 text-white text-xs label-tiny tracking-wider px-4 py-2" data-testid="sc-drill-submit">
              {saving ? "SAVING…" : "SAVE DRILL"}
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-sm text-slate-500">Loading drills…</div>
      ) : rows.length === 0 ? (
        <div className="border border-dashed border-slate-300 p-8 text-center" data-testid="sc-drills-empty">
          <Barbell size={28} weight="duotone" className="text-slate-400 mx-auto mb-2" />
          <div className="text-sm text-slate-500">No drills match those filters.</div>
        </div>
      ) : (
        <div className="space-y-6">
          {grouped.map(([sport, items]) => (
            <div key={sport}>
              <div className="label-tiny text-cyan-700 mb-2">// {sport.replace(/_/g, " ").toUpperCase()} · {items.length}</div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {items.map((d) => (
                  <div key={d.drill_id} onClick={() => setDetail(d)} className="border border-slate-200 bg-white hover:border-cyan-400 hover:bg-cyan-50 p-4 cursor-pointer transition-colors" data-testid={`sc-drill-card-${d.drill_id}`}>
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="font-heading text-sm">{d.name}</div>
                      {d.is_custom && <span className="text-[9px] bg-amber-100 text-amber-700 border border-amber-300 px-1 py-0.5">CUSTOM</span>}
                    </div>
                    <div className="text-[10px] tracking-widest text-slate-500 mb-2">
                      {(d.category || "").toUpperCase()} · {(d.difficulty || "").toUpperCase()} · {d.duration_min || "?"} min
                    </div>
                    {d.instructions && <div className="text-xs text-slate-600 line-clamp-2">{d.instructions}</div>}
                    {d.video_url && <div className="mt-2 flex items-center gap-1 text-xs text-cyan-600"><PlayCircle size={14} /> VIDEO</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Detail modal */}
      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={() => setDetail(null)} data-testid="sc-drill-detail">
          <div className="bg-white max-w-3xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <div className="font-heading text-xl">{detail.name}</div>
                <div className="text-[10px] tracking-widest text-cyan-700 mt-1">
                  {(detail.sport || "").replace(/_/g, " ").toUpperCase()} · {(detail.category || "").toUpperCase()} · {(detail.difficulty || "").toUpperCase()}
                </div>
              </div>
              <div className="flex items-center gap-3">
                {detail.is_custom && (
                  <button onClick={() => { removeDrill(detail.drill_id); setDetail(null); }} className="text-red-500 hover:text-red-700 text-xs flex items-center gap-1" data-testid="sc-drill-delete">
                    <Trash size={14} /> DELETE
                  </button>
                )}
                <button onClick={() => setDetail(null)} data-testid="sc-drill-detail-close"><X size={20} /></button>
              </div>
            </div>
            <div className="p-4 space-y-4">
              {detail.video_url && (
                <div className="aspect-video bg-black">
                  {detail.video_url.includes("youtube.com/embed") ? (
                    <iframe src={detail.video_url} title={detail.name} className="w-full h-full" allowFullScreen />
                  ) : (
                    <video src={detail.video_url.startsWith("http") ? detail.video_url : `${BACKEND_URL}${detail.video_url}`} controls className="w-full h-full" />
                  )}
                </div>
              )}
              <div className="grid grid-cols-3 gap-4 text-xs">
                <div><div className="label-tiny text-slate-500">DURATION</div><div className="font-heading">{detail.duration_min || "?"} MIN</div></div>
                <div><div className="label-tiny text-slate-500">PLAYERS</div><div className="font-heading">{detail.players || "—"}</div></div>
                <div><div className="label-tiny text-slate-500">EQUIPMENT</div><div className="font-heading text-[11px]">{(detail.equipment || []).join(", ") || "—"}</div></div>
              </div>
              {detail.instructions && (
                <div>
                  <div className="label-tiny text-slate-500 mb-1">INSTRUCTIONS</div>
                  <div className="text-sm text-slate-700 whitespace-pre-wrap">{detail.instructions}</div>
                </div>
              )}
              {detail.coach_cues?.length > 0 && (
                <div>
                  <div className="label-tiny text-slate-500 mb-1">COACH CUES</div>
                  <ul className="list-disc pl-5 text-sm text-slate-700 space-y-1">
                    {detail.coach_cues.map((c, i) => <li key={i}>{c}</li>)}
                  </ul>
                </div>
              )}
              {detail.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {detail.tags.map((t) => <span key={t} className="text-[10px] bg-slate-100 text-slate-700 px-1.5 py-0.5">#{t}</span>)}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
